import type { HouseBodyConfig, RoomConfig, RoomType } from '@/model/schema';
import type { ResolvedConstraints } from './constraints';
import type { Rng } from './rng';

/**
 * Rectangular recursive-split floor planner with grammar-biased typing.
 *
 * We use stylized planning per the design doc — rooms are axis-aligned
 * rectangles, no circulation geometry beyond a "hall" label. The goal is
 * communicating interior structure in top-down/sliced views, not navigable
 * architecture.
 *
 * Grammar
 * -------
 * Ground floor: living + kitchen + bath + hall
 * Upper floor:  bed + bed + bath + hall
 * Tall-compact: living → bath → bed stack across levels (handled by pipeline
 * passing different `level` intents)
 */

const MIN_ROOM_W = 2.4;
const MIN_ROOM_D = 2.4;

interface Rect {
  x: number; // lower-left corner in world-aligned footprint coords
  z: number;
  width: number;
  depth: number;
}

export interface PlanInput {
  level: number;
  body: HouseBodyConfig;
  constraints: ResolvedConstraints;
}

export function planStory(rng: Rng, input: PlanInput): RoomConfig[] {
  const { body, level, constraints } = input;

  // Footprint coords relative to the house body origin (lower-left).
  const root: Rect = {
    x: 0,
    z: 0,
    width: body.footprintWidth,
    depth: body.footprintDepth,
  };

  const grammar = grammarFor(level, constraints);
  const rects = splitRects(rng, root, grammar.length);

  const rooms: RoomConfig[] = [];
  const types = rng.shuffle(grammar);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i]!;
    const type: RoomType = types[i] ?? 'hall';
    rooms.push({
      id: `L${level}-${type}-${i}`,
      type,
      x: r.x,
      z: r.z,
      width: r.width,
      depth: r.depth,
      wallSegments: [],
    });
  }
  return rooms;
}

function grammarFor(level: number, c: ResolvedConstraints): RoomType[] {
  if (level === 0) {
    return ['living', 'kitchen', 'bathroom', 'hall'];
  }
  const bedsOnUpperFloors = Math.max(
    1,
    Math.round(c.bedrooms.value / Math.max(1, c.storyCount.value - 1)),
  );
  const rooms: RoomType[] = [];
  for (let i = 0; i < Math.min(3, bedsOnUpperFloors); i++) rooms.push('bedroom');
  rooms.push('bathroom');
  rooms.push('hall');
  return rooms;
}

/**
 * Recursive binary split of a rectangle into ~`count` sub-rectangles. We
 * split along the longer axis, with the split ratio biased toward 0.5 so
 * neither side becomes a slit. Splits that would produce sub-rectangles
 * below the minimum room size are rejected and the original kept whole.
 */
function splitRects(rng: Rng, root: Rect, count: number): Rect[] {
  const out: Rect[] = [root];
  let safety = 32;
  while (out.length < count && safety-- > 0) {
    // Pick the largest remaining rectangle.
    let idx = 0;
    let largest = 0;
    for (let i = 0; i < out.length; i++) {
      const r = out[i]!;
      const a = r.width * r.depth;
      if (a > largest) {
        largest = a;
        idx = i;
      }
    }
    const r = out[idx]!;
    const splitAlongX = r.width >= r.depth;
    const axisLen = splitAlongX ? r.width : r.depth;
    const minAxis = splitAlongX ? MIN_ROOM_W : MIN_ROOM_D;
    if (axisLen < minAxis * 2) {
      // Can't split this rectangle; bail out of the loop to avoid stalling.
      break;
    }
    const t = rng.range(0.38, 0.62);
    if (splitAlongX) {
      const w1 = r.width * t;
      out.splice(idx, 1,
        { x: r.x, z: r.z, width: w1, depth: r.depth },
        { x: r.x + w1, z: r.z, width: r.width - w1, depth: r.depth },
      );
    } else {
      const d1 = r.depth * t;
      out.splice(idx, 1,
        { x: r.x, z: r.z, width: r.width, depth: d1 },
        { x: r.x, z: r.z + d1, width: r.width, depth: r.depth - d1 },
      );
    }
  }
  return out;
}
